/**
 * Acoustic-aware prosody issue detector.
 *
 * Takes the WebAudio F0 analysis + ASR words/diff and produces a small
 * list of categorised issues that the resolver can match against
 * prosodyRef / phonicsRef i18n entries.
 *
 * Each category corresponds to either:
 *   - an existing prosodyRef.cats[K] (linguistic guidance the system
 *     already maintains), or
 *   - a synthetic acoustic key the resolver will surface as raw
 *     evidence to Gemini when no ref entry covers it.
 *
 * The detector is intentionally deterministic — Gemini is downstream
 * and only does the natural-language transcription. So if anything
 * here misfires, the suggestion misfires the same way every time and
 * we can debug it without prompt-replay.
 */
import type { AsrResult, DiffSummary } from '../../../services/asrCompareService';
import type { ProsodyAnalysis } from './pitchAnalysis';

export type ProsodyIssueCategory =
  // Acoustic — derived purely from F0 + voicing + timing
  | 'monotone'
  | 'sentence_end_fade'
  | 'fast_speech'
  | 'slow_speech'
  | 'hesitant'
  | 'low_voicing'
  // Mixed acoustic + sentence-type signal
  | 'rising_falling_mismatch'
  // Word-level — derived from WER diff (the user clearly said
  // something other than the reference word, or skipped one)
  | 'mispronunciation'
  | 'omission';

export type IssueSeverity = 'low' | 'medium' | 'high';

export interface DetectedIssue {
  category: ProsodyIssueCategory;
  severity: IssueSeverity;
  /** Free-form payload for the resolver / Gemini prompt to ground the
   *  suggestion in the actual numbers. */
  evidence: {
    metric?: string;
    value?: number;
    word?: string;
    wordIndex?: number;
    /** For mispronunciation: what the ASR thought it heard. */
    heardAs?: string;
  };
}

/**
 * How different two strings are, normalised by the longer one. Used
 * to grade mispronunciation severity — a tiny phoneme drop ("turnip"
 * → "tunip") is mid-severity, a full word swap ("turnip" → "tulip")
 * is also mid-, but a barely-recognisable mishearing is high.
 */
const normalisedEditDistance = (a: string, b: string): number => {
  const al = a.toLowerCase();
  const bl = b.toLowerCase();
  const m = al.length;
  const n = bl.length;
  if (m === 0 || n === 0) return Math.max(m, n);
  const dp = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const c = al[i - 1] === bl[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + c,
      );
    }
  }
  return dp[m][n] / Math.max(m, n);
};

const VOICING_PAD_SEC = 0.05; // standard mid-sentence tolerance
// Edge words (first / last) need much wider padding because ASR
// systematically clips their boundary — a learner's emphasised final
// vowel often sits *past* Deepgram's word.end by 100-300 ms and gets
// dropped if we only pad 50 ms. Bounded by the audio frame range so
// we don't run off either end.
const EDGE_PAD_SEC = 0.5;

const computeWordVoicedRatio = (
  wordStart: number,
  wordEnd: number,
  prosody: ProsodyAnalysis,
  /** Optional positional context — when supplied, we extend the
   *  outer boundary toward the audio edge / neighbour. */
  position?: { isFirst?: boolean; isLast?: boolean; prevEnd?: number; nextStart?: number },
): number | null => {
  const leftPad = position?.isFirst ? EDGE_PAD_SEC : VOICING_PAD_SEC;
  const rightPad = position?.isLast ? EDGE_PAD_SEC : VOICING_PAD_SEC;
  let ws = wordStart - leftPad;
  let we = wordEnd + rightPad;
  // Don't cross into a neighbour's tight window (still allow some
  // overlap via VOICING_PAD_SEC).
  if (position?.prevEnd != null) ws = Math.max(ws, position.prevEnd - VOICING_PAD_SEC);
  if (position?.nextStart != null) we = Math.min(we, position.nextStart + VOICING_PAD_SEC);
  let total = 0;
  let voiced = 0;
  for (let i = 0; i < prosody.frameTimes.length; i++) {
    const t = prosody.frameTimes[i];
    if (t < ws || t >= we) continue;
    total++;
    if (prosody.f0Frames[i] !== null) voiced++;
  }
  return total > 0 ? voiced / total : null;
};

const wordF0Mean = (
  wordStart: number,
  wordEnd: number,
  prosody: ProsodyAnalysis,
): number | null => {
  const ws = wordStart - VOICING_PAD_SEC;
  const we = wordEnd + VOICING_PAD_SEC;
  const hzList: number[] = [];
  for (let i = 0; i < prosody.frameTimes.length; i++) {
    const t = prosody.frameTimes[i];
    if (t < ws || t >= we) continue;
    const f = prosody.f0Frames[i];
    if (f !== null) hzList.push(f);
  }
  if (hzList.length === 0) return null;
  return hzList.reduce((a, b) => a + b, 0) / hzList.length;
};

const allVoicedF0Mean = (prosody: ProsodyAnalysis): number | null => {
  const voiced = prosody.f0Frames.filter((f): f is number => f !== null);
  if (voiced.length === 0) return null;
  return voiced.reduce((a, b) => a + b, 0) / voiced.length;
};

export interface DetectInput {
  prosody: ProsodyAnalysis;
  asr: AsrResult;
  diff: DiffSummary;
  referenceText: string;
}

export const detectProsodyIssues = ({
  prosody,
  asr,
  diff,
  referenceText,
}: DetectInput): DetectedIssue[] => {
  const issues: DetectedIssue[] = [];
  const words = asr.words || [];

  // ── 0. Word-level mispronunciation / omission (from diff) ─────────
  // The ASR transcribed something other than the reference for word
  // X, or skipped X entirely. Cap at 3 entries so the prompt doesn't
  // balloon for very-poor recordings.
  const MAX_WORD_ISSUES = 3;
  let wordIssueCount = 0;
  let refIdx = -1; // index into reference word stream (match/sub/delete advance)
  for (const entry of diff.entries) {
    const isRefSide = entry.op === 'match' || entry.op === 'substitute' || entry.op === 'delete';
    if (isRefSide) refIdx++;
    if (wordIssueCount >= MAX_WORD_ISSUES) continue;
    if (entry.op === 'substitute' && entry.ref && entry.hyp) {
      // Skip when the normalised forms (apostrophe + non-word stripped)
      // match — pure ASR tokenisation noise rather than a real
      // mispronunciation. "Lear's" vs "Lears" / "OK" vs "okay".
      const normRef = entry.ref.toLowerCase().replace(/['\W]/g, '');
      const normHyp = entry.hyp.toLowerCase().replace(/['\W]/g, '');
      if (normRef === normHyp) continue;
      const dist = normalisedEditDistance(entry.ref, entry.hyp);
      if (dist < 0.15) continue;
      issues.push({
        category: 'mispronunciation',
        severity: dist > 0.6 ? 'high' : 'medium',
        evidence: {
          word: entry.ref,
          heardAs: entry.hyp,
          wordIndex: refIdx,
        },
      });
      wordIssueCount++;
    } else if (entry.op === 'delete' && entry.ref) {
      // Skip "the" / "a" — short function-word deletions are usually
      // weak-form connected-speech, not "skipped a word" errors.
      const ref = entry.ref.toLowerCase().replace(/[^a-z']/g, '');
      if (ref.length <= 2 || ref === 'the' || ref === 'and' || ref === 'of') continue;
      issues.push({
        category: 'omission',
        severity: 'medium',
        evidence: {
          word: entry.ref,
          wordIndex: refIdx,
        },
      });
      wordIssueCount++;
    }
  }

  // ── 1. Monotone (pitch range collapsed) ────────────────────────────
  // Native English statement reads typically ~8 st range. <5 st is
  // perceptibly flat; <3 st is the "robot voice" zone.
  if (prosody.pitchRangeSt < 5) {
    issues.push({
      category: 'monotone',
      severity: prosody.pitchRangeSt < 3 ? 'high' : 'medium',
      evidence: { metric: 'pitch_range_st', value: prosody.pitchRangeSt },
    });
  }

  // ── 2. Sentence-end fade ──────────────────────────────────────────
  // Final word voiced ratio < 30% is almost always loss-of-volume
  // tail-off (vs a final word that genuinely ends in a fricative;
  // even those should hit ~40% voiced because the preceding vowel
  // contributes). Use isLast=true so the last-word window extends
  // past Deepgram's clipped word.end into any trailing voiced
  // emphasis the learner produced.
  if (words.length >= 2) {
    const last = words[words.length - 1];
    const prev = words[words.length - 2];
    if (last.start != null && last.end != null) {
      const lastVoiced = computeWordVoicedRatio(last.start, last.end, prosody, {
        isLast: true,
        prevEnd: prev.end,
      });
      if (lastVoiced != null && lastVoiced < 0.30) {
        issues.push({
          category: 'sentence_end_fade',
          severity: lastVoiced < 0.15 ? 'high' : 'medium',
          evidence: {
            word: last.word,
            wordIndex: words.length - 1,
            value: lastVoiced,
          },
        });
      }
    }
  }

  // ── 3. Fast / slow delivery ───────────────────────────────────────
  // Native conversational English ~3 wps (≈180 wpm). Reading aloud
  // tends slightly slower (2–3 wps). >4.5 wps is rushed; <1.5 wps
  // suggests over-deliberate / hesitant.
  const durSec = asr.durationSec
    || (words.length > 0 ? (words[words.length - 1].end ?? 1) : 1);
  const wordsPerSec = words.length / Math.max(durSec, 0.1);
  if (wordsPerSec > 4.5) {
    issues.push({
      category: 'fast_speech',
      severity: wordsPerSec > 6 ? 'high' : 'medium',
      evidence: { metric: 'words_per_sec', value: wordsPerSec },
    });
  } else if (wordsPerSec < 1.5) {
    issues.push({
      category: 'slow_speech',
      severity: 'medium',
      evidence: { metric: 'words_per_sec', value: wordsPerSec },
    });
  }

  // ── 4. Hesitant (multiple long inter-word pauses) ─────────────────
  let longGaps = 0;
  let firstLongGapWord: string | undefined;
  for (let i = 1; i < words.length; i++) {
    const prev = words[i - 1].end;
    const cur = words[i].start;
    if (prev != null && cur != null && cur - prev > 0.5) {
      longGaps++;
      if (firstLongGapWord == null) firstLongGapWord = words[i].word;
    }
  }
  if (longGaps >= 2) {
    issues.push({
      category: 'hesitant',
      severity: longGaps >= 3 ? 'high' : 'medium',
      evidence: {
        metric: 'long_gaps_count',
        value: longGaps,
        word: firstLongGapWord,
      },
    });
  }

  // ── 5. Overall low voicing (after browser NS/AGC are off) ─────────
  if (prosody.voicedRatio < 0.5) {
    issues.push({
      category: 'low_voicing',
      severity: prosody.voicedRatio < 0.35 ? 'high' : 'medium',
      evidence: { metric: 'voiced_ratio', value: prosody.voicedRatio },
    });
  }

  // ── 6. Rising / falling mismatch (sentence-type aware) ────────────
  // Heuristic: detect sentence type from end punctuation, then check
  // whether the final word's mean F0 sits above (rise) or below
  // (fall) the sentence's voiced mean.
  if (words.length >= 2) {
    const isQuestion = /\?\s*$/.test(referenceText.trim());
    const last = words[words.length - 1];
    if (last.start != null && last.end != null) {
      const lastMean = wordF0Mean(last.start, last.end, prosody);
      const allMean = allVoicedF0Mean(prosody);
      if (lastMean != null && allMean != null) {
        const diffSt = 12 * Math.log2(lastMean / allMean);
        if (isQuestion && diffSt < -0.8) {
          // Yes/no question should rise; user fell.
          issues.push({
            category: 'rising_falling_mismatch',
            severity: 'medium',
            evidence: {
              metric: 'question_should_rise_but_fell',
              value: diffSt,
              word: last.word,
            },
          });
        } else if (!isQuestion && diffSt > 1.2) {
          // Statement should fall; user ended noticeably higher.
          issues.push({
            category: 'rising_falling_mismatch',
            severity: 'medium',
            evidence: {
              metric: 'statement_should_fall_but_rose',
              value: diffSt,
              word: last.word,
            },
          });
        }
      }
    }
  }

  return issues;
};
